import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export interface OnlyFansSextfoceLiveStreamAnnoncementMessageGroup {
    _id?: string;
    subscriberId: string;
    active: boolean;
    name: string;
    sortOrder?: number;
    // New fields for per-group intervals
    announcementEveryAmount: number;
    announcementEveryUnit: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface OnlyFansSextfoceLiveStreamAnnouncementMessage {
    _id?: string;
    groupId: string;
    active: boolean;
    name: string;
    message: string;
    sortOrder?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface OnlyFansSextfoceLiveStreamAnnoncementMessageGroupWithMessages extends OnlyFansSextfoceLiveStreamAnnoncementMessageGroup {
    messages: OnlyFansSextfoceLiveStreamAnnouncementMessage[];
}

const useSextforceLiveStreamAutoAnnouncementsMessages = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();
    const params = useParams();
    const axios = useAxios();

    // Fetch the restricted words
    const fetchData = async (): Promise<OnlyFansSextfoceLiveStreamAnnoncementMessageGroupWithMessages[]> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/groups`;

            return axios
                .get(query)
                .then(response => response.data)
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);

                    return [];
                });
        }

        return [];
    };

    const liveStreams = useQuery(
        ['liveStreamsAutoAnnouncementsMessages', userContext.jwtToken, params.userId],
        () => {
            return fetchData();
        },
        {
            refetchOnWindowFocus: false,
            // Stake time 5 minutes
            staleTime: 5 * 60 * 1000,
            enabled: userContext.jwtToken && settingsContext.apiKey && userContext.user.username && params.userId ? true : false,
        },
    );

    // Create a new message
    const [isSextforceAutoAnnouncementMessageCreateLoading, setIsSextforceAutoAnnouncementMessageCreateLoading] = useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageCreate = async (
        newMessage: OnlyFansSextfoceLiveStreamAnnouncementMessage,
    ): Promise<OnlyFansSextfoceLiveStreamAnnouncementMessage | null> => {
        setIsSextforceAutoAnnouncementMessageCreateLoading(true);

        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/messages`;

            return axios
                .post(query, newMessage)
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageCreateLoading(false);
                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                    setIsSextforceAutoAnnouncementMessageCreateLoading(false);
                    return null;
                });
        }

        setIsSextforceAutoAnnouncementMessageCreateLoading(false);

        return null;
    };

    // Update a message
    const [isSextforceAutoAnnouncementMessageUpdateLoading, setIsSextforceAutoAnnouncementMessageUpdateLoading] = useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageUpdate = async (
        messageId: string,
        newMessage: OnlyFansSextfoceLiveStreamAnnouncementMessage,
    ): Promise<OnlyFansSextfoceLiveStreamAnnouncementMessage | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/messages/${messageId}`;

            setIsSextforceAutoAnnouncementMessageUpdateLoading(true);

            return axios
                .put(query, newMessage)
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageUpdateLoading(false);

                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);

                    setIsSextforceAutoAnnouncementMessageUpdateLoading(false);

                    return null;
                });
        }

        setIsSextforceAutoAnnouncementMessageUpdateLoading(false);

        return null;
    };

    // Delete a message
    const [isSextforceAutoAnnouncementMessageDeleteLoading, setIsSextforceAutoAnnouncementMessageDeleteLoading] = useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageDelete = async (messageId: string): Promise<boolean> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/messages/${messageId}`;

            setIsSextforceAutoAnnouncementMessageDeleteLoading(true);

            return axios
                .delete(query)
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageDeleteLoading(false);
                    return response.data?.success || false;
                })
                .catch(error => {
                    console.error(error);
                    setIsSextforceAutoAnnouncementMessageDeleteLoading(false);
                    handleHttpError(error, dialog);

                    return false;
                });
        }

        return false;
    };

    // Update the sort order of messages
    const [isSextforceAutoAnnouncementMessageSortOrderUpdateLoading, setIsSextforceAutoAnnouncementMessageSortOrderUpdateLoading] =
        useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageSortOrderUpdate = async (
        groupId: string,
        newMessagesOrder: { messageId: string; sortOrder: number }[],
    ): Promise<boolean> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/groups/${groupId}/sortOrder`;

            setIsSextforceAutoAnnouncementMessageSortOrderUpdateLoading(true);

            return axios
                .put(query, { newMessagesOrder })
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageSortOrderUpdateLoading(false);
                    return response.data?.success || false;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                    setIsSextforceAutoAnnouncementMessageSortOrderUpdateLoading(false);
                    return false;
                });
        }

        setIsSextforceAutoAnnouncementMessageSortOrderUpdateLoading(false);
        return false;
    };

    // Set a message as active or inactive
    const [isSextforceAutoAnnouncementMessageActiveUpdateLoading, setIsSextforceAutoAnnouncementMessageActiveUpdateLoading] =
        useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageActiveUpdate = async (messageId: string, isActive: boolean): Promise<boolean> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/messages/${messageId}/active`;

            setIsSextforceAutoAnnouncementMessageActiveUpdateLoading(true);

            return axios
                .put(query, { active: isActive })
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageActiveUpdateLoading(false);
                    return response.data?.success || false;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                    setIsSextforceAutoAnnouncementMessageActiveUpdateLoading(false);
                    return false;
                });
        }

        setIsSextforceAutoAnnouncementMessageActiveUpdateLoading(false);
        return false;
    };

    // Create a new message group
    const [isSextforceAutoAnnouncementMessageGroupCreateLoading, setIsSextforceAutoAnnouncementMessageGroupCreateLoading] =
        useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageGroupCreate = async (
        newGroup: OnlyFansSextfoceLiveStreamAnnoncementMessageGroup,
    ): Promise<OnlyFansSextfoceLiveStreamAnnoncementMessageGroup | null> => {
        setIsSextforceAutoAnnouncementMessageGroupCreateLoading(true);

        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/groups`;

            return axios
                .post(query, newGroup)
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageGroupCreateLoading(false);
                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                    setIsSextforceAutoAnnouncementMessageGroupCreateLoading(false);
                    return null;
                });
        }

        setIsSextforceAutoAnnouncementMessageGroupCreateLoading(false);

        return null;
    };

    // Update a message group
    const [isSextforceAutoAnnouncementMessageGroupUpdateLoading, setIsSextforceAutoAnnouncementMessageGroupUpdateLoading] =
        useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageGroupUpdate = async (
        groupId: string,
        updatedGroup: OnlyFansSextfoceLiveStreamAnnoncementMessageGroup,
    ): Promise<OnlyFansSextfoceLiveStreamAnnoncementMessageGroup | null> => {
        setIsSextforceAutoAnnouncementMessageGroupUpdateLoading(true);

        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/groups/${groupId}`;

            return axios
                .put(query, updatedGroup)
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageGroupUpdateLoading(false);
                    return response.data;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                    setIsSextforceAutoAnnouncementMessageGroupUpdateLoading(false);
                    return null;
                });
        }

        setIsSextforceAutoAnnouncementMessageGroupUpdateLoading(false);

        return null;
    };

    // Delete a message group
    const [isSextforceAutoAnnouncementMessageGroupDeleteLoading, setIsSextforceAutoAnnouncementMessageGroupDeleteLoading] =
        useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageGroupDelete = async (groupId: string): Promise<boolean> => {
        setIsSextforceAutoAnnouncementMessageGroupDeleteLoading(true);

        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/groups/${groupId}`;

            return axios
                .delete(query)
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageGroupDeleteLoading(false);
                    return response.status === 204;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                    setIsSextforceAutoAnnouncementMessageGroupDeleteLoading(false);
                    return false;
                });
        }

        setIsSextforceAutoAnnouncementMessageGroupDeleteLoading(false);

        return false;
    };

    // Update the sort order of message groups
    const [
        isSextforceAutoAnnouncementMessageGroupSortOrderUpdateLoading,
        setIsSextforceAutoAnnouncementMessageGroupSortOrderUpdateLoading,
    ] = useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageGroupSortOrderUpdate = async (
        newGroupsOrder: { groupId: string; sortOrder: number }[],
    ): Promise<boolean> => {
        setIsSextforceAutoAnnouncementMessageGroupSortOrderUpdateLoading(true);

        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/groups/sortOrder`;

            return axios
                .put(query, { newGroupsOrder })
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageGroupSortOrderUpdateLoading(false);
                    return response.status === 204;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                    setIsSextforceAutoAnnouncementMessageGroupSortOrderUpdateLoading(false);
                    return false;
                });
        }

        setIsSextforceAutoAnnouncementMessageGroupSortOrderUpdateLoading(false);

        return false;
    };

    // Set the active status of a message group
    const [isSextforceAutoAnnouncementMessageGroupActiveUpdateLoading, setIsSextforceAutoAnnouncementMessageGroupActiveUpdateLoading] =
        useState<boolean>(false);

    const sextforceLiveStreamAutoAnnouncementMessageGroupActiveUpdate = async (groupId: string, active: boolean): Promise<boolean> => {
        setIsSextforceAutoAnnouncementMessageGroupActiveUpdateLoading(true);

        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.sextforce.base}${params.userId}/liveStream/autoAnnouncements/groups/${groupId}/active`;

            return axios
                .put(query, { active })
                .then(response => {
                    setIsSextforceAutoAnnouncementMessageGroupActiveUpdateLoading(false);
                    return response.data?.success || false;
                })
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);
                    setIsSextforceAutoAnnouncementMessageGroupActiveUpdateLoading(false);
                    return false;
                });
        }

        setIsSextforceAutoAnnouncementMessageGroupActiveUpdateLoading(false);

        return false;
    };

    // Return all the hooks and functions
    return {
        ...liveStreams,
        isSextforceAutoAnnouncementMessageCreateLoading,
        sextforceLiveStreamAutoAnnouncementMessageCreate,
        isSextforceAutoAnnouncementMessageUpdateLoading,
        sextforceLiveStreamAutoAnnouncementMessageUpdate,
        isSextforceAutoAnnouncementMessageDeleteLoading,
        sextforceLiveStreamAutoAnnouncementMessageDelete,
        isSextforceAutoAnnouncementMessageSortOrderUpdateLoading,
        sextforceLiveStreamAutoAnnouncementMessageSortOrderUpdate,
        isSextforceAutoAnnouncementMessageActiveUpdateLoading,
        sextforceLiveStreamAutoAnnouncementMessageActiveUpdate,
        isSextforceAutoAnnouncementMessageGroupCreateLoading,
        sextforceLiveStreamAutoAnnouncementMessageGroupCreate,
        isSextforceAutoAnnouncementMessageGroupUpdateLoading,
        sextforceLiveStreamAutoAnnouncementMessageGroupUpdate,
        isSextforceAutoAnnouncementMessageGroupDeleteLoading,
        sextforceLiveStreamAutoAnnouncementMessageGroupDelete,
        isSextforceAutoAnnouncementMessageGroupSortOrderUpdateLoading,
        sextforceLiveStreamAutoAnnouncementMessageGroupSortOrderUpdate,
        isSextforceAutoAnnouncementMessageGroupActiveUpdateLoading,
        sextforceLiveStreamAutoAnnouncementMessageGroupActiveUpdate,
    };
};

export default useSextforceLiveStreamAutoAnnouncementsMessages;
